import type { AuthProvider, UserIdentity } from 'ra-core';
import { sessionCourante, deconnexion } from '@groupe-trousseau/shared/api-client';
import { adresseDeConnexion, deballer } from './client';
import { peut, type RoleAdministrant } from './matrice';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **L'AUTHENTIFICATION — ET `admin/` N'A AUCUN ÉCRAN DE CONNEXION.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `CDC-5` F-55 : cette surface « **s'atteint par la page de connexion unique du site
 * vitrine, celle des quatre rôles**. **Aucune adresse cachée, aucun sélecteur de rôle** ».
 * Et `CDC-6` F-71 place cet écran sur `site/`, page 6 de sa liste fermée de huit.
 *
 * Le motif est écrit dans `site/src/features/connexion/connexion.ts` et il vaut ici :
 * « **une seconde route réservée à l'administration aurait suffi à trahir quelles adresses
 * en sont** ». La peau livre bien une `LoginPage` — `Administration.tsx` la remplace par un
 * renvoi, et c'est la seule pièce de la peau qu'on écarte.
 *
 * **`login` n'existe donc pas ici**, au sens où elle ne prend aucun identifiant : elle
 * change d'adresse. F-71 : « à travers cette page, ce qui est retenu est une
 * **DESTINATION, JAMAIS UNE VALEUR QUI VAUT ACCÈS** ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LE RÔLE VIENT DU SERVEUR À CHAQUE FOIS, ET N'EST JAMAIS RECOPIÉ.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `GET /api/v1/auth/session` rend le compte et ses rattachements actifs. Rien n'est mis en
 * réserve dans le navigateur : `DOCTRINE.md` §6 — « aucune donnée serveur recopiée dans un
 * store client : cela crée **deux sources de vérité et des affichages périmés** ». Ici la
 * donnée périmée serait **un rôle**, et un rôle périmé est exactement ce que la matrice
 * existe pour ne pas laisser arriver.
 *
 * C'est `ra-core` qui met en cache la réponse de `getIdentity` et de `canAccess`, par
 * TanStack Query, avec l'invalidation qui va avec — donc à un seul endroit, celui qui sait
 * l'invalider.
 */

/**
 * **Le rattachement d'un `orgadmin` est SA structure, et il en a au plus un.**
 *
 * `CDC-5` §3 : « le périmètre d'un `orgadmin` est SA structure, sur chaque ligne, sans
 * exception. Un compte `orgadmin` porte **au plus un rattachement** — exactement un tant
 * qu'il tient un espace, zéro une fois retiré. **Aucun autre rôle du produit ne porte cette
 * contrainte** » (Q534).
 *
 * Et l'autre moitié, §3 encore : « **le compte qui voit tout n'a jamais de rattachement, et
 * il entre quand même** » (Q535) — l'exception explicite à la règle qui exige un
 * rattachement actif pour entrer. C'est pourquoi `accesPerdu` ne se lit pas comme un refus
 * pour lui : il est vrai par construction sur ce compte-là.
 */
export type Identite = UserIdentity & {
  readonly role: RoleAdministrant;
  /** La structure d'un `orgadmin`, ou `null` pour le compte qui voit tout. */
  readonly structureDeRattachement: string | null;
};

const ROLES_ADMINISTRANTS: readonly string[] = ['ORGADMIN', 'SUPERADMIN'];

export const fournisseurDAuthentification: AuthProvider = {
  /**
   * **Se connecter, c'est aller sur le site vitrine.** Aucun identifiant ne transite ici.
   * `ra-core` appelle `login` quand un écran l'y invite ; il n'y en a aucun, mais la
   * fonction est requise par l'interface et le renvoi est ce qu'elle doit faire.
   */
  login: () => {
    // **Sans `VITE_SITE_URL`, on ne redirige nulle part.** `adresseDeConnexion()` rend `null`
    // quand elle manque, et rediriger sur `'/'` faute de mieux était la boucle infinie
    // constatée en recette le 20260824. `ra-core` affiche alors la page de renvoi, qui nomme
    // la variable manquante — un écran qui explique vaut mieux qu'un rechargement sans fin.
    const adresse = adresseDeConnexion();
    if (adresse !== null) window.location.replace(adresse);
    return Promise.resolve();
  },

  /**
   * **Se déconnecter appelle le serveur PUIS renvoie sur la page de connexion.**
   *
   * L'appel au serveur n'est pas une politesse : la session vit dans un cookie `httpOnly`
   * que ce code **ne peut pas effacer** — c'est tout le point de D-065. Oublier l'appel
   * laisserait la session vivante et donnerait l'illusion d'une déconnexion.
   *
   * `CDC-6` : « **se déconnecter ramène sur la page de connexion, pas sur l'accueil du
   * site** ». L'échec de l'appel ne bloque pas le renvoi : une session qu'on n'a pas pu
   * fermer ne doit pas retenir la personne sur la surface.
   */
  logout: async () => {
    try {
      await deconnexion();
    } catch {
      // Le renvoi a lieu quand même : voir ci-dessus.
    }
    // **Même garde qu'à l'entrée**, et elle compte autant : une déconnexion qui renvoie sur
    // `'/'` rentre dans la surface qu'on vient de quitter, et repart en boucle.
    const adresse = adresseDeConnexion();
    if (adresse !== null) window.location.replace(adresse);
  },

  /**
   * **La garde d'entrée, et elle refuse deux choses distinctes.**
   *
   * 1. **Personne n'est connecté** → renvoi sur la page de connexion de `site/`.
   * 2. **Un `tutor` ou un `student` est connecté** → F-55 critère 1 : « SI un compte
   *    `tutor` ou `student` ouvre une adresse de la surface d'administration, ALORS LE
   *    SYSTÈME DOIT le rejeter avec l'erreur `SURFACE_ADMINISTRATION_INTERDITE` », et §3
   *    insiste : « cette ligne est écrite **en refus explicite plutôt que déduite du
   *    silence** ».
   *
   * **Le refus qui compte est celui du serveur**, `SurfaceAdministrationGuard`, posé
   * globalement et sur toute adresse de la surface — y compris celles qui n'existent pas
   * encore, ce qui est son motif. Celui-ci est **l'écran** de ce refus : sans lui, un
   * tuteur connecté verrait une coquille d'administration se remplir de `403`.
   *
   * **On ne le renvoie PAS vers la connexion** : il est connecté, et l'y renvoyer le
   * ferait tourner en rond entre deux surfaces. Il voit le refus, nommé.
   */
  checkAuth: async () => {
    const session = await deballer(sessionCourante());
    if (!ROLES_ADMINISTRANTS.includes(session.compte.role)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw {
        code: 'SURFACE_ADMINISTRATION_INTERDITE',
        message:
          'Cette surface est réservée aux comptes qui administrent une structure ou le parc.',
        // `redirectTo: false` retient la personne sur la page de refus au lieu de
        // l'envoyer sur un écran de connexion qu'elle vient de traverser.
        redirectTo: false,
        logoutUser: false,
      };
    }
  },

  /**
   * **Ce qu'on fait d'une erreur d'API, et ce qu'on n'en fait pas.**
   *
   * Un `401` est une session tombée, et il n'y a que trois causes possibles — expirée,
   * révoquée, compte anonymisé —, **que le front ne distingue pas parce que le serveur ne
   * les distingue pas non plus** : « un refus qui nommerait sa cause dirait à un attaquant
   * laquelle il a déclenchée ».
   *
   * **Un `403` ne déconnecte pas**, et c'est la distinction qui compte sur cette surface :
   * `CDC-5` en nomme quatre — `SURFACE_ADMINISTRATION_INTERDITE`,
   * `REGLAGE_DE_PARC_INTERDIT`, `CHIFFRES_DE_PARC_INTERDITS`,
   * `AUCUN_ELEVE_DE_CETTE_STRUCTURE`. Les trois derniers arrivent à **une personne
   * légitimement connectée qui a touché la borne de son rôle ou de son périmètre** ; la
   * déconnecter là ferait passer une borne pour une panne de session.
   */
  checkError: (erreur: unknown) => {
    const statut = (erreur as { status?: number; statusCode?: number } | null)?.status;
    const statutBis = (erreur as { statusCode?: number } | null)?.statusCode;
    if (statut === 401 || statutBis === 401) return Promise.reject(new Error('session'));
    return Promise.resolve();
  },

  /**
   * **L'identité, et les deux choses que la surface en tire.**
   *
   * Le **rôle**, qui décide de tout par la matrice ; et **la structure de rattachement**
   * d'un `orgadmin`, qui est son périmètre entier. Le compte qui voit tout n'en a aucune, et
   * choisit une **structure courante** par le sélecteur de F-55 — « le passage d'une
   * structure à l'autre est un sélecteur permanent en tête de la surface ». « **La personne
   * qui tient un espace n'a pas de sélecteur**, portant au plus un rattachement : il n'aurait
   * qu'une ligne. »
   */
  getIdentity: async (): Promise<Identite> => {
    const session = await deballer(sessionCourante());
    const role = session.compte.role as RoleAdministrant;
    const premier = session.rattachementsActifs[0];
    return {
      id: session.compte.id,
      fullName:
        [session.compte.prenom, session.compte.nom].filter(Boolean).join(' ') ||
        (session.compte.email ?? 'Compte'),
      role,
      structureDeRattachement: role === 'ORGADMIN' ? (premier?.organisationId ?? null) : null,
    };
  },

  /**
   * **LA MATRICE §6.14 EST LUE ICI, ET NULLE PART AILLEURS.**
   *
   * `ra-core` appelle `canAccess` avant d'afficher une ressource, un bouton de la peau ou un
   * geste métier. C'est ce qui permet à `matrice.ts` d'être **une table** et non des
   * conditions éparpillées — le motif entier de §6.14 : « une condition enfouie dans un
   * service ne se relit jamais en revue ».
   */
  canAccess: async ({ resource, action }: { resource: string; action: string }) => {
    const session = await deballer(sessionCourante());
    const role = session.compte.role;
    if (!ROLES_ADMINISTRANTS.includes(role)) return false;
    return peut(role as RoleAdministrant, resource, action);
  },
};
